// src/pages/portal/index.jsx
//
// Portal del cliente: SOLO sus instrumentos y certificados (la base lo garantiza
// por RLS — acá no hay que filtrar nada). Muestra vigencia, descarga del
// certificado aprobado y, si el equipo está ahora en el laboratorio, en qué
// etapa está (con etiquetas pensadas para el cliente, no la jerga interna).

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/system/AppGate';
import { supabase } from '../../utils/supabase';
import { instrumentTypeLabel } from '../../utils/instrument-types';

// Estado interno → qué le decimos al cliente.
const LAB_STATUS = {
  pending_reception: { label: 'Por enviar al laboratorio', cls: 'border-slate-500/30 bg-slate-500/10 text-slate-300' },
  active:            { label: 'En el laboratorio · recibido', cls: 'border-sky-500/30 bg-sky-500/10 text-sky-300' },
  in_progress:       { label: 'En el laboratorio · en calibración', cls: 'border-amber-500/30 bg-amber-500/10 text-amber-300' },
  paused:            { label: 'En el laboratorio · en espera', cls: 'border-amber-500/30 bg-amber-500/10 text-amber-300' },
  calibrated:        { label: 'En el laboratorio · calibrado', cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  with_certificate:  { label: 'En el laboratorio · calibrado', cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  without_certificate:{ label: 'En el laboratorio · calibrado', cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  rejected:          { label: 'En el laboratorio · observado', cls: 'border-rose-500/30 bg-rose-500/10 text-rose-300' },
  reviewed:          { label: 'En el laboratorio · aprobado', cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  ready_to_remit:    { label: 'Listo para retirar / envío', cls: 'border-teal-500/30 bg-teal-500/10 text-teal-300' },
  remitted:          { label: 'Remitido · en camino', cls: 'border-teal-500/30 bg-teal-500/10 text-teal-300' },
};

const VIG = {
  vigente:        { label: 'VIGENTE', cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' },
  por_vencer:     { label: 'POR VENCER', cls: 'border-amber-500/40 bg-amber-500/10 text-amber-300' },
  vencido:        { label: 'VENCIDO', cls: 'border-rose-500/40 bg-rose-500/10 text-rose-300' },
  sin_certificado:{ label: 'SIN CERTIFICADO', cls: 'border-slate-500/30 bg-slate-500/10 text-slate-400' },
  sin_vencimiento:{ label: 'SIN VENCIMIENTO', cls: 'border-slate-500/30 bg-slate-500/10 text-slate-400' },
};

function fmtDate(v) {
  if (!v) return '—';
  const [y, m, d] = String(v).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : '—';
}

export default function PortalPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [rows, setRows] = useState(null);
  const [clientName, setClientName] = useState('');
  const [q, setQ] = useState('');
  const [soloVencidos, setSoloVencidos] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // RLS: ambas consultas devuelven solo lo del cliente logueado.
        const [{ data: search }, { data: cli }] = await Promise.all([
          supabase.from('v_certificate_search').select('*').order('lab_id', { ascending: true }),
          supabase.from('clients').select('name').limit(1),
        ]);
        if (!alive) return;
        setRows(search || []);
        setClientName(cli?.[0]?.name || '');
      } catch (e) {
        console.error('[Portal]', e);
        if (alive) setRows([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/portal/login', { replace: true });
  };

  const visible = useMemo(() => {
    let out = rows || [];
    const t = q.trim().toLowerCase();
    if (t) {
      out = out.filter((r) =>
        [r.serial, r.tag, r.brand, r.model, r.certificate_number, String(r.lab_id)]
          .some((v) => v && String(v).toLowerCase().includes(t))
      );
    }
    if (soloVencidos) out = out.filter((r) => r.vigencia === 'vencido' || r.vigencia === 'por_vencer');
    return out;
  }, [rows, q, soloVencidos]);

  const stats = useMemo(() => {
    const r = rows || [];
    return {
      total: r.length,
      vigentes: r.filter((x) => x.vigencia === 'vigente').length,
      alerta: r.filter((x) => x.vigencia === 'por_vencer' || x.vigencia === 'vencido').length,
      enLab: r.filter((x) => LAB_STATUS[x.instrument_status] && x.instrument_status !== 'pending_reception').length,
    };
  }, [rows]);

  // Cuenta creada pero sin empresa vinculada (email no autorizado aún).
  if (profile && profile.role === 'client' && !profile.client_id) {
    return (
      <div className="min-h-screen bg-[#0b0f14] text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-3">
          <div className="text-3xl">🔒</div>
          <h1 className="text-lg font-semibold">Tu cuenta todavía no está habilitada</h1>
          <p className="text-sm text-slate-400">
            Escribinos a <a className="text-sky-400" href="mailto:laboratorio@calins.com.ar">laboratorio@calins.com.ar</a> para
            vincular tu email con tu empresa.
          </p>
          <button onClick={handleLogout} className="text-sm text-slate-400 underline">Cerrar sesión</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0f14] text-white">
      {/* Header propio del portal (sin la navegación interna del laboratorio) */}
      <header className="border-b border-white/10 bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-xl font-bold tracking-tight">Calins</div>
            <div className="text-xs text-slate-400">Portal de clientes{clientName ? ` · ${clientName}` : ''}</div>
          </div>
          <button onClick={handleLogout} className="px-3 py-1.5 rounded-lg border border-white/15 text-sm text-slate-300 hover:bg-white/5">
            Salir
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 space-y-5">
        {/* Resumen */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ['Instrumentos', stats.total, 'text-white'],
            ['Vigentes', stats.vigentes, 'text-emerald-300'],
            ['Por vencer / vencidos', stats.alerta, stats.alerta ? 'text-amber-300' : 'text-slate-400'],
            ['En el laboratorio', stats.enLab, 'text-sky-300'],
          ].map(([label, val, cls]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
              <div className={`text-xl font-bold ${cls}`}>{rows === null ? '…' : val}</div>
            </div>
          ))}
        </div>

        {/* Búsqueda */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <span className="absolute inset-y-0 left-3 flex items-center text-slate-500 pointer-events-none">🔍</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por serie, TAG, marca, modelo o n° de certificado…"
              className="w-full rounded-xl bg-slate-800/60 border border-white/10 pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={soloVencidos} onChange={(e) => setSoloVencidos(e.target.checked)} />
            Solo por vencer / vencidos
          </label>
        </div>

        {/* Lista */}
        {rows === null ? (
          <div className="text-sm text-slate-400 py-8 text-center">Cargando tus instrumentos…</div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-8 text-center text-slate-400">
            {rows.length === 0 ? 'Todavía no hay instrumentos registrados para tu empresa.' : 'Sin resultados para esa búsqueda.'}
          </div>
        ) : (
          <div className="space-y-2.5">
            {visible.map((r) => {
              const vig = VIG[r.vigencia] || VIG.sin_certificado;
              const lab = LAB_STATUS[r.instrument_status];
              return (
                <div key={r.instrument_id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-white">#{r.lab_id}</span>
                        <span className="text-slate-200">{[r.brand, r.model].filter(Boolean).join(' ') || instrumentTypeLabel(r.type)}</span>
                        {r.tag && <span className="text-[11px] bg-white/10 px-2 py-0.5 rounded-full text-slate-300">TAG: {r.tag}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {instrumentTypeLabel(r.type)}{r.serial ? ` · Serie ${r.serial}` : ''}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${vig.cls}`}>
                          {vig.label}{r.valid_until ? ` · hasta ${fmtDate(r.valid_until)}` : ''}
                        </span>
                        {lab && (
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${lab.cls}`}>{lab.label}</span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      {r.certificate_number ? (
                        <>
                          <div className="text-xs text-slate-500">Certificado</div>
                          <div className="font-semibold text-white">N° {r.certificate_number}</div>
                          <div className="text-[11px] text-slate-500">{fmtDate(r.calibration_date)}</div>
                          {r.pdf_url && (
                            <a
                              href={r.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1.5 inline-block px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-xs font-semibold transition"
                            >
                              📄 Descargar
                            </a>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-600">Sin certificado emitido</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-[11px] text-slate-600 text-center pt-2">
          ¿Dudas con un certificado? Escribinos a laboratorio@calins.com.ar
        </p>
      </main>
    </div>
  );
}
